"use client";

import React, { useEffect } from "react";
import { VideoContent } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import "plyr/dist/plyr.css";

interface VideoPlayerProps {
  blok?: VideoContent & SbBlokData;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ blok }) => {
  useEffect(() => {
    if (!blok?.tag) return;

    let player: any = null;

    const initializePlayer = async () => {
      if (typeof window !== 'undefined') {
        const { default: Plyr } = await import('plyr');
        const element = document.querySelector('[data-plyr-provider="youtube"]');

        if (element) {
          player = new Plyr(element as HTMLElement, {
            controls: ['play-large', 'play', 'progress', 'current-time', 'mute', 'volume', 'fullscreen'],
            youtube: {
              noCookie: true,
              rel: 0,
              showinfo: 0,
              iv_load_policy: 3,
              modestbranding: 1,
              lazy: true,
              loading: 'lazy'
            }
          });
        }
      }
    };

    initializePlayer();

    return () => {
      if (player?.destroy) {
        player.destroy();
      }
    };
  }, [blok?.tag]);

  if (!blok?.tag) return null;

  const videoSchema = {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    "name": blok.name || blok.title || "",
    "description": blok.description || "",
    "thumbnailUrl": `https://img.youtube.com/vi/${blok.tag}/maxresdefault.jpg`,
    "uploadDate": blok.uploadDate || new Date().toISOString(),
    "duration": blok.duration || "PT0M0S",
    "publisher": {
      "@type": "Organization",
      "name": "Bulletproof",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.bulletproof.co.uk/images/bp-structured-data-logo.png"
      }
    },
    "contentUrl": `https://www.youtube.com/watch?v=${blok.tag}`,
    "embedUrl": `https://www.youtube.com/embed/${blok.tag}`
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify(videoSchema)}
      </script>
      <div {...storyblokEditable(blok)} className="video-responsive w-100 rounded-3 shadow overflow-hidden">
        <div
          data-plyr-provider="youtube"
          data-plyr-embed-id={blok.tag}
        ></div>
      </div>
    </>
  );
};

export default VideoPlayer;